import React, { useContext, useState } from "react";
import { useTranslation, useI18next } from "gatsby-plugin-react-i18next";
import MenuToolTipBody from "components/Design/MenuToolTipBody/MenuTooltipBody";
import { BaliPropertyToolTipBody } from "components/Design/MenuToolTipBody/MenuTooltipBody";
import { MenuToolTipBodyItem } from "components/Design/MenuToolTipBody/MenuTooltipBody";
import { TopBarItem } from "components/TopBar";
import { MenuListItem } from "components/Design/MenuList/MenuList";
import MenuList from "components/Design/MenuList/MenuList";
import NavBar from "components/Design/NavBar/NavBar";
import Icon from "components/CommonIcon/Icon";
import { embedLanguageInUrl } from "utils/embedLanguageInUrl";
import { AYANA_BALI } from "store/constants/hotel";
import SupportDrawer from "components/SupportDrawer/SupportDrawer";
import { useLocation } from "@reach/router";
import useDrawer from "hooks/useDrawer";
import { HeaderLogo } from "components/Logo/AyanaLogo";
import { AyanaLogo } from "components/Logo/AyanaLogo";
import { CardHotel } from "components/Design/Card";
import HOTEL_PROPERTY_BALI_URL from "dictionaries/hotelBali";
import HotelSelectionDrawer from "components/HotelSelectionDrawer";
import HOTELS from "dictionaries/hotelLink";
import PageContext from "contexts/PageContext";
import Tooltip from "./Design/Tooltip/Tooltip";
import { AnnouncementStrip } from "./AnnouncementStrip";
import { getSessionId } from "utils/session";

const mapPartiallyActive = (menu, pathname) => {
  if (
    menu.text === "Experiences" &&
    pathname.includes("/labuan-bajo/komodo/experience/boat-charter/")
  ) {
    return false;
  }

  return menu.partiallyActive;
};

const NavBarContent = ({
  menus,
  supportDrawer,
  supportReachUsLink,
  navigationHotel,
  hotelPropertyBali,
  pathname,
  highlight,
}) => {
  const { t } = useTranslation("homepage");
  const {
    open: supportDrawerOpen,
    close: supportDrawerClose,
    isOpen: supportDrawerIsOpen,
  } = useDrawer();

  const { language } = useI18next();
  const menuItems = menus?.filter((f) => f.dropdownItems === false);
  const menuItemsWithDropdown = menus
    ?.filter((f) => f.dropdownItems === true)
    .map((menu) => {
      if (menu.text === "Ayana Support") {
        return {
          ...menu,
          drawerOpen: supportDrawerOpen,
        };
      } else if (menu.link.includes("http")) {
        return { ...menu, link: embedLanguageInUrl(menu.link, language) };
      } else {
        return { ...menu };
      }
    });

  return (
    <>
      {navigationHotel === AYANA_BALI && (
        <Tooltip
          trigger={<span className="tracking-16">{t("Hotels")}</span>}
          icon={
            <Icon
              name="chevron-down"
              color="text-gray-text"
              fontSize="text-12"
            />
          }
          openOnHover>
          <div className="text-black capitalize">
            <BaliPropertyToolTipBody>
              <div className="flex flex-col normal-case">
                {hotelPropertyBali.map((hotel, idx) => {
                  return (
                    <div
                      className={`relative border-b border-solid border-gray-light py-12 after:w-70 after:absolute after:bg-white after:h-[1.1px] after:left-0 after:-bottom-1  ${
                        idx === 0 && "pt-0"
                      } ${
                        idx + 1 === hotelPropertyBali.length &&
                        "pb-0 border-none"
                      }`}>
                      <CardHotel
                        logo={hotel.logo}
                        title={hotel.title}
                        to={HOTEL_PROPERTY_BALI_URL[hotel.name]}
                      />
                    </div>
                  );
                })}
              </div>
            </BaliPropertyToolTipBody>
          </div>
        </Tooltip>
      )}
      {menuItems?.map((menu) => (
        <TopBarItem
          to={menu.link}
          isHighlighted={menu.highlightKey === highlight}
          partiallyActive={
            highlight ? false : mapPartiallyActive(menu, pathname)
          }
          activeClassName={!highlight && "topbar-item-active"}
          newTab={menu.newTab}>
          {t(menu.text)}
        </TopBarItem>
      ))}
      <Tooltip
        trigger={<span className="tracking-16">{t("More")}</span>}
        icon={
          <Icon name="chevron-down" color="text-gray-text" fontSize="text-12" />
        }
        openOnHover>
        <div className="text-black capitalize">
          <MenuToolTipBody title={t("More Information")}>
            {menuItemsWithDropdown?.map((menu) => {
              return (
                <MenuToolTipBodyItem
                  icon={<Icon name={menu.iconName} fontSize="text-16" />}
                  to={menu.drawer ? null : menu.link}
                  onClick={menu.drawer ? menu.drawerOpen : null}>
                  {t(menu.text)}
                </MenuToolTipBodyItem>
              );
            })}
            <SupportDrawer
              visible={supportDrawerIsOpen}
              close={supportDrawerClose}
              content={supportDrawer}
              supportReachUsLink={supportReachUsLink}
            />
          </MenuToolTipBody>
        </div>
      </Tooltip>
    </>
  );
};

const MenuListContent = ({ menus, hotelPropertyBali, pathname, highlight }) => {
  const { t } = useTranslation("homepage");

  const {
    open: hotelSelectionDrawerOpen,
    close: hotelSelectionDrawerClose,
    isOpen: hotelSelectionDrawerIsOpen,
  } = useDrawer();

  const menuItemsNew = menus
    ?.filter((f) => f.dropdownItems === false)
    .map((menu) => {
      if (menu.text === "Hotels") {
        return {
          ...menu,
          drawerOpen: hotelSelectionDrawerOpen,
        };
      } else {
        return { ...menu };
      }
    });

  return (
    <>
      <MenuList>
        {menuItemsNew?.map((menu) => (
          <MenuListItem
            to={menu.drawer ? null : menu.link}
            isHighlighted={menu.highlightKey === highlight}
            partiallyActive={
              highlight || menu.drawer
                ? null
                : mapPartiallyActive(menu, pathname)
            }
            activeClassName={!highlight && "text-primary"}
            newTab={menu.drawer ? menu.newTab : null}
            onClick={menu.drawer ? menu.drawerOpen : null}>
            {t(menu.text)}
          </MenuListItem>
        ))}
      </MenuList>
      {hotelPropertyBali && (
        <HotelSelectionDrawer
          visible={hotelSelectionDrawerIsOpen}
          close={hotelSelectionDrawerClose}
          content={hotelPropertyBali}
        />
      )}
    </>
  );
};

const MoreMenuListContent = ({
  menuItems,
  supportDrawer,
  supportReachUsLink,
}) => {
  const { t } = useTranslation("homepage");

  const {
    open: supportDrawerOpen,
    close: supportDrawerClose,
    isOpen: supportDrawerIsOpen,
  } = useDrawer();

  const { language } = useI18next();

  const menuItemsWithDropdown = menuItems
    ?.filter((f) => f.dropdownItems === true)
    .map((menu) => {
      if (menu.text === "Ayana Support") {
        return {
          ...menu,
          drawerOpen: supportDrawerOpen,
        };
      } else if (menu.link.includes("http")) {
        return { ...menu, link: embedLanguageInUrl(menu.link, language) };
      } else if (menu.text === "Hotels") {
        return {
          ...menu,
          drawerOpen: supportDrawerOpen,
        };
      } else {
        return { ...menu };
      }
    });

  return (
    <>
      {menuItemsWithDropdown.map((menu) => {
        return (
          <MenuToolTipBodyItem
            icon={<Icon name={menu.iconName} fontSize="text-16" />}
            to={menu.drawer ? null : menu.link}
            onClick={menu.drawer ? menu.drawerOpen : null}>
            {t(menu.text)}
          </MenuToolTipBodyItem>
        );
      })}
      <SupportDrawer
        visible={supportDrawerIsOpen}
        close={supportDrawerClose}
        content={supportDrawer}
        supportReachUsLink={supportReachUsLink}
      />
    </>
  );
};

export const Navbar = ({
  destinationCards,
  contactOnDesktop,
  cartCount,
  navigations,
  hotel,
  supportDrawer,
  supportReachUsLink,
  hotelPropertyBali,
  restaurant,
  destination,
  minimumStay,
  whatsappLink,
  bookingCode,
  stayPeriod,
  isPackage,
  highlight,
  location: locationFromProps,
  packageCode,
  announcementStrip,
  hotelId,
  roomId,
  minLeadDay,
}) => {
  const { language } = useI18next();
  const { location } = useContext(PageContext);
  const { pathname } = useLocation();

  const bookingEngineLink = `${process.env.GATSBY_BOOKING_ENGINE_URL}${
    language ? `${language}` : ""
  }/cart?sessionId=${getSessionId()}`;

  const ColorLogo = () => {
    return <AyanaLogo image={hotel.logo_horizontal[0]} />;
  };

  const menuItemsForDesktop = navigations.menuItem.filter(
    (f) => f.devices.includes("desktop") && f.navbar.includes("header")
  );
  const menuItemsForMobile = navigations.menuItem.filter(
    (f) => f.devices.includes("mobile") && f.navbar.includes("header")
  );

  let linkLogo;

  if (location || locationFromProps) {
    linkLogo = HOTELS[location || locationFromProps];
  } else {
    linkLogo = HOTELS[hotel.name];
  }
  const [stripShow, setStripShow] = useState(true);

  return (
    <div className="sticky top-0 lg:top-34 z-90 duration-500 md:duration-800 bg-white shadow-nav-header">
      {stripShow && announcementStrip && (
        <AnnouncementStrip
          onClose={() => setStripShow(false)}
          description={announcementStrip.description}
          url={announcementStrip.redirection_url}
        />
      )}
      <div className="absolute top-0 left-0 -z-1 w-full h-120 invisible" />
      <div className="w-full antialiased">
        <NavBar
          hotelId={hotelId}
          roomId={roomId}
          logo={<HeaderLogo hotel={hotel} />}
          destinationCards={destinationCards}
          location={hotel.location}
          bookingEngineLink={bookingEngineLink}
          cartCount={cartCount}
          menuList={
            <MenuListContent
              highlight={highlight}
              menus={menuItemsForMobile}
              hotelPropertyBali={hotelPropertyBali}
              pathname={pathname}
            />
          }
          moreMenuListItem={
            <MoreMenuListContent
              menuItems={menuItemsForMobile}
              supportDrawer={supportDrawer}
              supportReachUsLink={supportReachUsLink}
            />
          }
          contactOnDesktop={contactOnDesktop}
          navbarMobileLogo={<ColorLogo />}
          logoLink={linkLogo}
          restaurant={restaurant}
          destination={destination}
          whatsappContact={hotel.whatsapp_contact}
          whatsappLink={whatsappLink}
          hotelCode={hotel.hotel_code}
          minimumStay={minimumStay}
          stayPeriod={stayPeriod}
          isPackage={isPackage}
          bookingCode={bookingCode}
          packageCode={packageCode}
          minLeadDay={minLeadDay}
          hotel={hotel}>
          <NavBarContent
            menus={menuItemsForDesktop}
            supportDrawer={supportDrawer}
            supportReachUsLink={supportReachUsLink}
            navigationHotel={navigations.hotel}
            hotelPropertyBali={hotelPropertyBali}
            pathname={pathname}
            highlight={highlight}
          />
        </NavBar>
      </div>
    </div>
  );
};
